import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePaymentForm } from "customHook/usePaymentForm";
import SubHeader from "components/SubHeader";
import FooterBar from "components/FooterBar";
import Button from "components/Button";
import PaymentMethod from "./PaymentMethod";
import BookingSetUp from "./BookingSetUp";
import AddOns from "./AddOns";
//import GeneralGroupNotesLocal from "./GeneralGroupNotesLocal";
import BookingBreakdown from "components/BookingMain/BookingBreakdown";
import ModalBookingNotComplete from "screens/ModalBookingNotComplete";
import {
  CREATE_GROUP_BOOKING,
  // CREATE_GROUP_NOTE,
  // UPDATE_GROUP_BY_GROUP_ID,
  GET_ALL_BOOKING_ADD_ONS,
} from "graphql/service";
import { useMutation, useQuery } from "@apollo/client";
import {
  Content,
  ContentColumn,
  Container,
} from "components/BookingMain/styles";
import moment from "moment-timezone";
import { TIME_ZONE } from "constants/constant";
import { debouncedErrorMessage } from "utils/messageUtil";
import {
  DEFAULT_CHECK_IN_HOUR,
  DEFAULT_CHECK_IN_MINUTE,
  DEFAULT_CHECK_OUT_HOUR,
  DEFAULT_CHECK_OUT_MINUTE,
} from "constants/constant";

const CreateBooking = () => {

  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);

  //PaymentMethod
  const paymentInfo = useSelector(
    (state) => state.paymentInfoReducer.paymentInfo
  );
  const [creditCardType, setCreditCardType] = useState("");
  const { isValidatePaymentInfo } = usePaymentForm(creditCardType);

  //GroupBookingSetUp
  const [checkInDate, setCheckInDate] = useState(
    moment()
      .tz(TIME_ZONE)
      .hour(DEFAULT_CHECK_IN_HOUR)
      .minute(DEFAULT_CHECK_IN_MINUTE)
      .second(0)
  );
  const [checkOutDate, setCheckOutDate] = useState(
    moment()
      .tz(TIME_ZONE)
      .add(1, "days")
      .hour(DEFAULT_CHECK_OUT_HOUR)
      .minute(DEFAULT_CHECK_OUT_MINUTE)
      .second(0)
  );
  const [roomQuantity, setRoomQuantity] = useState("");
  const [roomType, setRoomType] = useState({});
  const [ratePlan, setRatePlan] = useState({});
  const [coupon, setCoupon] = useState({});
  const [isWheelchairAccessible, setIsWheelchairAccessible] = useState(false);

  const [breakfastQuantity, setBreakfastQuantity] = useState(0);
  const [breakfastLength, setBreakfastLength] = useState(1);;
  const [parkingQuantity, setParkingQuantity] = useState(0);
  const [parkingLength, setParkingLength] = useState(1);

  const { data: addOnData } = useQuery(GET_ALL_BOOKING_ADD_ONS, {
    fetchPolicy: "cache-and-network"
  });

  const breakfastUnitPrice = useMemo(() => {
    return addOnData?.validBookingAddons?.find(
      (item) => item.title === "Breakfast"
    )?.pricePerNight;
  }, [addOnData]);

  const parkingUnitPrice = useMemo(() => {
    return addOnData?.validBookingAddons?.find(
      (item) => item.title === "Parking"
    )?.pricePerNight;
  }, [addOnData]);

  //GuestInformation
  const [customerInfo, setCustomerInfo] = useState({
    organization: "",
    phoneNumber: "",
    email: "",
  });

  const [customerInfoError, setCustomerInfoError] = useState({});

  // const [createGroupNote] = useMutation(CREATE_GROUP_NOTE)
  // const [updateGroupByGroupId] = useMutation(UPDATE_GROUP_BY_GROUP_ID)
  const [createGroupBooking, { loading }] = useMutation(CREATE_GROUP_BOOKING, {
    onCompleted: (createGroupBookingData) => {
      if (createGroupBookingData.createGroupBooking) {
        const groupId = createGroupBookingData.createGroupBooking.bookings[0].group.groupId
        navigate(`/booking-details/${groupId}`);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const handleCreate = () => {
    if (loading) return;
    let result = true;

    if (!customerInfo.email) {
      result = false;
      setCustomerInfoError((customerInfoError) => ({
        ...customerInfoError,
        emailError: true,
      }));
    } else {
      setCustomerInfoError((customerInfoError) => ({
        ...customerInfoError,
        emailError: false,
      }));
    }

    if (!customerInfo.phoneNumber) {
      result = false;
      setCustomerInfoError((customerInfoError) => ({
        ...customerInfoError,
        phoneNumberError: true,
      }));
    } else {
      setCustomerInfoError((customerInfoError) => ({
        ...customerInfoError,
        phoneNumberError: false,
      }));
    }
    if (result && isValidatePaymentInfo()) {

      const group = {
        fullName: paymentInfo.cardHolderName,
        email: customerInfo.email,
        phoneNumber: customerInfo.phoneNumber,
        checkInDate: checkInDate,
        checkOutDate: checkOutDate,
        roomTypeId: roomType.roomTypeId,
        numberOfRooms: Number(roomQuantity),
        numberOfGuests: Number(roomQuantity),
        addons: [
          {
            bookingAddOnId: "63f6e8f8-8095-4c79-8d92-ce5812db4f15",
            quantity: breakfastQuantity,
            numberOfNights: breakfastLength,
          },
          {
            bookingAddOnId: "fd67a4cb-667a-4ba9-bec4-379cb430682a",
            quantity: parkingQuantity,
            numberOfNights: parkingLength,
          },
        ],
        isWheelchairAccessible: isWheelchairAccessible,
        ratePlanId: ratePlan.ratePlanId,
        organization: customerInfo.organization,
        billingInfo: {
          creditCardNumber: paymentInfo.creditCardNumber.replace(/\s/g, ""),
          cardExpirationDate: paymentInfo.cardExpirationDate.replace(
            /[\s/]/g,
            ""
          ),
          cardCVV: paymentInfo.cardCVV,
          cardHolderName: paymentInfo.cardHolderName,
          streetAddress: paymentInfo.streetAddress,
          streetAddressLine2: paymentInfo.streetAddressLine2,
          city: paymentInfo.city,
          province: paymentInfo.province,
          postalCode: paymentInfo.postalCode,
          country: paymentInfo.country,
        },
      };
      createGroupBooking({
        variables: {
          input: group,
        },
      });
    } else {
      setIsOpen(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Booking - Rook Hotel PMS</title>
        <meta name="description" content="Create Booking - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className="title">
          <Link
            className="highlight"
            to={"/bookings"}
          >
            Bookings
          </Link>
          {" > Create Group Booking"}
        </div>
        <div className="buttonContainer">
          <div>
          </div>
        </div>
      </SubHeader>
      <ModalBookingNotComplete isOpen={isOpen} setIsOpen={setIsOpen} />
      <Content
        additionalstyle={`
          margin-top: 100px;
        `}
      >
        <ContentColumn>
          <Container>
            <BookingSetUp
              checkInDate={checkInDate}
              setCheckInDate={setCheckInDate}
              checkOutDate={checkOutDate}
              setCheckOutDate={setCheckOutDate}
              roomQuantity={roomQuantity}
              setRoomQuantity={setRoomQuantity}
              roomType={roomType}
              setRoomType={setRoomType}
              ratePlan={ratePlan}
              setRatePlan={setRatePlan}
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              customerInfoError={customerInfoError}
              setCustomerInfoError={setCustomerInfoError}
              isWheelchairAccessible={isWheelchairAccessible}
              setIsWheelchairAccessible={setIsWheelchairAccessible}
            />
          </Container>
          <Container>
            <PaymentMethod
              creditCardType={creditCardType}
              setCreditCardType={setCreditCardType}
            />
          </Container>
          <Container>
            <AddOns
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              breakfastQuantity={breakfastQuantity}
              setBreakfastQuantity={setBreakfastQuantity}
              breakfastLength={breakfastLength}
              setBreakfastLength={setBreakfastLength}
              breakfastUnitPrice={breakfastUnitPrice}
              parkingQuantity={parkingQuantity}
              setParkingQuantity={setParkingQuantity}
              parkingLength={parkingLength}
              setParkingLength={setParkingLength}
              parkingUnitPrice={parkingUnitPrice}
              coupon={coupon}
              setCoupon={setCoupon}
            />
          </Container>
        </ContentColumn>
        <ContentColumn>
          {/* <Container>
            <GeneralGroupNotesLocal userId={userId} fullName={fullName}/>
          </Container> */}
          <Container>
            <BookingBreakdown
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              roomQuantity={roomQuantity}
              roomType={roomType}
              ratePlan={ratePlan}
              coupon={coupon}
              breakfastTotal={breakfastQuantity * breakfastLength}
              breakfastUnitPrice={breakfastUnitPrice}
              parkingTotal={parkingQuantity * parkingLength}
              parkingUnitPrice={parkingUnitPrice}
            />
          </Container>
          <Container>
            <div style ={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center" 
            }}>
              <Button
                mainPage
                success
                onClick={handleCreate}
                data-loading={loading}
                additionalstyle={`
                  width: 170px;
                `}
              >
                {loading ? null : "Create and Charge Card"}
              </Button>
            </div>
          </Container>
        </ContentColumn>
      </Content>
      <FooterBar />
    </>
  );
};

export default CreateBooking;
